import { Executor, ExecutorLocations } from "../../services";
import { getExecutorStatusLabel } from "../../hooks/useExecutorStatus";
import { store } from "../../redux/store";
import i18n from "../../utils/i18n";
import { formatNumber } from "../../utils";

import { isExecutorMapGpsFresh } from "./gpsFresh.util";

const escapeHtml = (value: string) =>
	value
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;");

const row = (label: string, value: string) =>
	`<div>${escapeHtml(label)}: ${escapeHtml(value)}</div>`;

const findExecutor = (id: number): Executor.Model | undefined => {
	const list =
		store.getState().globalState.executors.data.subscription.basic || [];
	return list.find((item) => item.id === id);
};

const personName = (data: Executor.Model): string =>
	`${data?.person?.lastName ? data.person.lastName : ""} ${
		data?.person?.firstName ? data.person.firstName : ""
	} ${data?.person?.fatherName ? data.person.fatherName : ""}`;

const personPhones = (data: Executor.Model): string =>
	data.person?.phones?.map((phone) => phone.number)?.join(", ") || "";

const carFields = (
	data: Executor.Model,
	lang: string,
): { carData: string; carType: string; parkNumber: string } => {
	const carShiftJournals = data.executorShiftJournals?.length
		? data.executorShiftJournals[0]?.car
		: null;
	const executorToCrews = data?.executorToCrews || [];
	const crew = carShiftJournals
		? executorToCrews.find(
				({ crew: item }) => item.car.id === carShiftJournals.id,
		  )?.crew
		: data.executorToCrews?.[0]?.crew;
	const car = crew ? crew?.car : data.executorToCrews?.[0]?.crew?.car;
	const registrationNumber = car?.additionalFields?.registrationNumber || "";
	const color = car?.color?.name?.[lang] || "";
	const parkNumber = car?.callSign || "";

	return {
		carData: `${
			car?.modelToBodyType?.model?.baseTypeToBrand?.brand?.name || ""
		} ${car?.modelToBodyType?.model?.name || ""} ${color || ""} ${
			registrationNumber ? `(${registrationNumber})` : ""
		}`,
		carType: `${car?.class?.name?.[lang] || ""}`,
		parkNumber,
	};
};

const t = (key: string) => {
	const lng = store.getState().session.language;
	return i18n.t(key, { lng }) ?? "";
};

// eslint-disable-next-line import/prefer-default-export
export const executorTooltipHtml = (
	location: ExecutorLocations.Model,
): string => {
	const id = location.executor?.id ?? location.executorId;
	const executor = id ? findExecutor(id) : undefined;
	if (!executor) return "";

	const lang = store.getState().session.language;
	const speed = Number((location.speed || 0) * 3.6).toFixed(1);
	const rating = location?.executor?.rating || location?.rating || 0;

	const labels = {
		alias: t("orderPageWidgets.parking.parkingTable.str209"),
		fullName: t("orderPageWidgets.parking.parkingTable.str210"),
		status: t("orderPageWidgets.parking.parkingTable.str211"),
		phones: t("orderPageWidgets.parking.parkingTable.str212"),
		carData: t("utils.getTabsModeOne.cars"),
		carType: t("class"),
		online: t("orderPageWidgets.parking.parkingTable.str214"),
		balance: t("balances.main"),
		speed: t("orderPageWidgets.parking.parkingTable.str220"),
		priority: t(
			"pages.settings.pages.orders.tabs.ordersDistribution.modal.options.priority",
		),
		kph: t("units.kph"),
		taxiService: t("taxiService"),
		company: t("company"),
		parkNumber: t("accessText.parkNumber"),
		rating: t("utils.getTabsModeOne.ratingPoints"),
	};

	const statuses = {
		busy: t("orderPageWidgets.parking.parkingTable.str202") ?? "",
		dinner: t("orderPageWidgets.parking.parkingTable.str203") ?? "",
		home: t("orderPageWidgets.parking.parkingTable.str204") ?? "",
		on_order: t("orderPageWidgets.parking.parkingTable.str205") ?? "",
		available: t("orderPageWidgets.parking.parkingTable.str206") ?? "",
		closed: t("orderPageWidgets.parking.parkingTable.str207") ?? "",
		ownOrder: t("orderPageWidgets.parking.parkingTable.str208") ?? "",
		debt: t("orderPageWidgets.executors.str206") ?? "",
	};

	const car = carFields(executor, lang);
	const main =
		executor.paymentAccounts?.find((account) => account.type === "main")
			?.amount ?? 0;
	const online = executor.online
		? t("orderPageWidgets.parking.parkingTable.str200") ?? ""
		: t("orderPageWidgets.parking.parkingTable.str201") ?? "";

	const staleNote = !isExecutorMapGpsFresh(location)
		? `<div>${escapeHtml(
				t("orderPageWidgets.parking.parkingTable.str221"),
		  )}</div>`
		: "";

	return `<div>${staleNote}${row(labels.alias, executor.alias ?? "")}${row(
		labels.status,
		getExecutorStatusLabel(executor, statuses),
	)}${row(labels.online, online)}${row(
		labels.fullName,
		personName(executor),
	)}${row(labels.phones, personPhones(executor))}${row(
		labels.parkNumber,
		car.parkNumber,
	)}${row(labels.carData, car.carData)}${row(
		labels.carType,
		car.carType,
	)}${row(labels.speed, `${speed} ${labels.kph}`)}${row(
		labels.balance,
		formatNumber(main) || "",
	)}${row(labels.priority, String(executor.priority || 0))}${row(
		labels.rating,
		String(rating),
	)}${row(
		labels.company,
		executor?.taxiService?.company?.name?.[lang] || "",
	)}${row(
		labels.taxiService,
		executor?.taxiService?.settlement?.[lang] || "",
	)}</div>`;
};
